import React, { FC, useEffect } from "react";

// hooks
import useMediaQuery from "@UI/hooks/useMediaQuery";

// Freemium
import { FAQSection } from "@modules/CustomerSupport/Components/FAQSection/FAQSection";

// i18n
import messages from "./messages";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

// UI
import {
  Wizard,
  WizardHeader,
  WizardContent,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import {
  SideBySide,
  SideBySideDivider,
  SideBySideLeft,
  SideBySideRight,
} from "@Shared/Components/Layouts/SideBySide/SideBySide";
import { Heading } from "@UI/Components/Heading/Heading";
import { Flex } from "@UI/Components/Flex/Flex";
import { Container } from "@UI/Components/Container/Container";
import { Box } from "@UI/Components/Box/Box";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";

// Core
import { useParams } from "@Libs/Router/hooks/useParams";

// ServiceOffers
import { useServiceOffer } from "@modules/ServiceAssignment/hooks/useServiceOffer";

// Hooks
import { useRecordEvent } from "@Libs/Events/hooks/useRecordEvent";
import { ServiceAssignmentEventStep } from "@modules/ServiceAssignment/types/serviceAssignment.types";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import useCookies from "@Libs/Utils/hooks/useCookies";
import { useServiceProvider } from "@modules/ServiceProviders/hooks/useServiceProvider";
import { SubscriptionFormulaCard } from "@modules/Subscriptions/Components/SubscriptionFormulaCard/SubscriptionFormulaCard";
import { ServiceFeeFormulaCard } from "@modules/ServiceFees/Components/ServiceFeeFormulaCard/ServiceFeeFormulaCard";
import { Or } from "@UI/Components/Or/Or";
import { useFeatureFlag } from "@Libs/GrowthBook/hooks/useFeatureFlag";
import { trackEvent } from "@Libs/GrowthBook/services/growthbook";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { ChooseFormulaPageActivationRate } from "./ABTests/ActivationRate/ChooseFormulaPageActivationRate";

export const ChooseFormulaPage: FC = () => {
  const { isMobile } = useMediaQuery();
  const { serviceOfferId } = useParams([
    "SERVICE_OFFERS",
    "SERVICE_OFFER",
    "ACCEPTANCE",
    "CHOOSE_FORMULA",
  ]);
  const navigate = useNavigate();
  const cookies = useCookies();

  const { serviceOffer, isLoading: isServiceOfferLoading } = useServiceOffer(
    serviceOfferId,
    {
      includes: ["expenses"],
    }
  );

  const { serviceProvider, isLoading: isServiceProviderLoading } =
    useServiceProvider(serviceOffer?.serviceProviderId, {
      includes: ["user"],
    });

  const { currentUser } = useCurrentUser();

  const { recordEvent, isCurrentUserLoaded } = useRecordEvent(
    {
      key: "contract_acceptance_flow.step_visited",
      payload: {
        data: {
          contractId: Number(serviceOfferId),
          step: ServiceAssignmentEventStep.CHOOSE_FORMULA,
        },
      },
    },
    { triggerOnMount: false }
  );

  useEffect(() => {
    if (!isCurrentUserLoaded) return;
    recordEvent();
    // activation_rate experiment events — safe to remove once experiment is concluded
    trackEvent("choose_formula_page_visited", {
      contractId: Number(serviceOfferId),
      serviceRequestId: serviceOffer?.serviceRequestId,
      clientId: currentUser?.id,
      clientEmail: currentUser?.email,
      isSubscribed: currentUser?.subscribed ?? false,
    });
  }, [isCurrentUserLoaded]);

  const backParamCookie = cookies.get("back_param");

  const handleBack = () => {
    if (backParamCookie === "instant_matching") {
      const destination = "INSTANT_MATCHING_URL";

      navigate(destination, {
        reload: true,
        queryParams: {
          task_id: String(serviceOffer.serviceRequestId),
        },
      });
    } else {
      navigate("LEGACY_SERVICE_REQUEST", {
        reload: true,
        params: { id: String(serviceOffer.serviceRequestId) },
        queryParams: {
          conversation: String(serviceOffer.conversationId),
          anchor: "questions",
        },
      });
    }
  };

  const isActivationRateEnabled = useFeatureFlag("new-activation-rate");

  const handleFormulaCardClick = (formula: "subscription" | "service_fee") => {
    const baseProps = {
      contractId: Number(serviceOfferId),
      serviceRequestId: serviceOffer?.serviceRequestId,
      clientId: currentUser?.id,
      clientEmail: currentUser?.email,
      isSubscribed: currentUser?.subscribed ?? false,
    };
    trackEvent("formula_card_selected", { ...baseProps, formula });
    trackEvent("choose_formula_next_clicked", { ...baseProps, formula });
  };

  if (isServiceOfferLoading || isServiceProviderLoading) {
    return <LoadingScreen />;
  }

  if (isActivationRateEnabled) {
    return (
      <ChooseFormulaPageActivationRate
        serviceOffer={serviceOffer}
        serviceProvider={serviceProvider}
        serviceOfferId={serviceOfferId}
        onBack={handleBack}
      />
    );
  }

  const serviceProviderName = serviceProvider?.user?.firstName;

  return (
    <>
      <Wizard>
        <WizardNav>
          <BackLink onClick={handleBack} />
        </WizardNav>
        <WizardHeader>
          <Flex justifyContent="center">
            <Heading as="h3" paddingRight={{ base: 6, md: 0 }} flexShrink={1}>
              <FormattedMessage
                {...messages.chooseBetweenTheTwoFormulas}
                values={{ serviceProviderName }}
              />
            </Heading>
          </Flex>
        </WizardHeader>
        <WizardContent>
          <Flex justifyContent="center">
            <Box
              maxW={["100%", "100%", "100%", "500px"]}
              minW={["100%", "100%", "100%", "470px"]}
            >
              {/* <ServiceFeePropositionCard
                serviceOffer={serviceOffer}
                subscriptionLink={{
                  route: [
                    "SERVICE_OFFERS",
                    "SERVICE_OFFER",
                    "ACCEPTANCE",
                    "SUBSCRIBE",
                  ],
                  params: {
                    serviceOfferId: String(serviceOffer?.id),
                  },
                }}
                standardFeeLink={{
                  route: [
                    "SERVICE_OFFERS",
                    "SERVICE_OFFER",
                    "ACCEPTANCE",
                    "SERVICE_FEE",
                  ],
                  params: {
                    serviceOfferId: String(serviceOffer?.id),
                  },
                }}
              />*/}
            </Box>
          </Flex>
          <SideBySide>
            <SideBySideLeft>
              <Box onClick={() => handleFormulaCardClick("subscription")} height="100%">
              <SubscriptionFormulaCard
                link={{
                  route: [
                    "SERVICE_OFFERS",
                    "SERVICE_OFFER",
                    "ACCEPTANCE",
                    "SUBSCRIBE",
                    "CHOOSE_PLAN",
                  ],
                  params: {
                    serviceOfferId: String(serviceOffer?.id),
                  },
                }}
              />
              </Box>
            </SideBySideLeft>
            <SideBySideRight>
              <Box onClick={() => handleFormulaCardClick("service_fee")} height="100%">
              <ServiceFeeFormulaCard
                serviceProvider={serviceProvider}
                serviceOffer={serviceOffer}
                link={{
                  route: [
                    "SERVICE_OFFERS",
                    "SERVICE_OFFER",
                    "ACCEPTANCE",
                    "SERVICE_FEE",
                  ],
                  params: {
                    serviceOfferId: String(serviceOffer?.id),
                  },
                }}
              />
              </Box>
            </SideBySideRight>
            <SideBySideDivider>
              <Or orientation="vertical" />
            </SideBySideDivider>
          </SideBySide>
          {isMobile && (
            <Box marginTop="10">
              <FAQSection />
            </Box>
          )}
        </WizardContent>
      </Wizard>
      {!isMobile && (
        <Container
          marginY={[10, 10, 20]}
          maxW={["container.lg", "container.md", "container.lg"]}
        >
          <FAQSection />
        </Container>
      )}
    </>
  );
};
